import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./style.css";

// Components
import FormSections from "../../common/FormApproveComponents/FormSections/FormSections";
import Tab from "apollo-react/components/Tab";
import Tabs from "apollo-react/components/Tabs";
import StarSolid from "apollo-react-icons/StarSolid";
import ClarificationWaves from "../../common/ClarificationWaves";

// Custom hooks
import {
  useFormData,
  useFormActions,
  useActiveTab,
  useRequestDetails,
} from "../../../hooks";
import { ROLES } from "../../../constants/roles";
import { loadTreeData } from "../../common/ECTD/constants";
import Ectd from "../../common/ECTD";
import CoreECTD from "../../common/CoreECTD";
import AuditTrails from "../../common/AuditTrails";
import { STATUSES } from "../../../constants/statuses";
import { APPROVAL_TYPE_CODES } from "../../../constants/approvalTypes";
import ActionButtons from "../../common/FormApproveComponents/ActionButtons/ActionButtons";
import DrugPricingFormView from "../../Guest/Requests/New_Requests/DrugPricingForm/DrugPricingFormView";
import AdditionalDocuments from "../../common/AdditionalDocuments/AdditionalDocuments";
import Committee from "../../common/Committee/Committee";
import {  getCurrentUser as getCurrentUserAdmin } from "../services/authService";
import {  getCurrentUser as getCurrentUserCustomer } from "../../Guest/services/authService";
import CommitteeReviewButtons from "../../common/FormApproveComponents/CommitteeReviewButtons";
import { CATEGORIES } from "../../../constants/categories";

// Wrapper component for persona validation
const FormApprove = ({ persona, isStudyCompleted }) => {
  
  // Persona validation - before any hooks are called
  if (!persona) {
    return <div>Error: Persona not passed contact dev team.</div>;
  }

  // Render the main component only when persona is available
  return <FormApproveMain persona={persona} isStudyCompleted={isStudyCompleted} />;
};

// Main component safe to be used with persona passed in
const FormApproveMain = ({ persona, isStudyCompleted }) => {
  const { t } = useTranslation();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const { id: requestProgressId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [comments, setComments] = useState([]);
  const [labComments, setLabComments] = useState([]);
  const [inspectionComments, setInspectionComments] = useState([]);
  const [additionalDocsComments, setAdditionalDocsComments] = useState([]);

  const [fileUploadTrigger, setFileUploadTrigger] = useState(0);
  const [treeData, setTreeData] = useState([]);

  // Load tree data on mount
  useEffect(() => {
    const fetchTreeData = async () => {
      const data = await loadTreeData();
      setTreeData(data);
    };
    fetchTreeData();
  }, []);

  // Custom hooks
  const {
    filledForm,
    subForms,
    children,
    requestProgress,
    loading,
    parentStage
  } = useFormData(requestProgressId, persona);
  const updatedChildren = [...children];

  if(currentUserRole === ROLES.core_Validator &&
    requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.core_Validator].ACTIVE) {
      updatedChildren.push({
        id: 'coreValidator',
        approval_type_id: 4,
        ApprovalType: {
          id: 4,
          name: 'Send To Reviewers',
          code: APPROVAL_TYPE_CODES.COMMITTEE
        },
      });
    }

  const { handleAction } = useFormActions();
  const requestDetails = useRequestDetails(requestProgress?.request_id, persona) || {};

  const [TABS, setTABS] = useState([]);
  const [activeTab, setActiveTab] = useActiveTab(TABS);
  
  // Handlers
  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  useEffect(() => {
    // Fetch current user data if needed
    const fetchCurrentUser = async () => {
      try {
			const response =
				persona === "customer"
					? await getCurrentUserCustomer()
					: await getCurrentUserAdmin();
			
			// Extract role name as string
			let roleName;
			if (persona === "customer") {
			  roleName = response.end_users_category?.name;
			} else {
			  roleName = response.user?.Role?.name;
			}
			
			setCurrentUserRole(roleName);
		} catch (error) {
			console.error("Error fetching current user:", error);
		}
    };

    fetchCurrentUser();
  }, [persona, setActiveTab, currentUserRole]);

  useEffect(() => {
    const openRelativeTab = (currentTabs) => {
      switch (currentUserRole) {
        case ROLES.technical_validator:
          return currentTabs.findIndex(tab => tab.value === 1);
        case ROLES.pricing:
          return currentTabs.findIndex(tab => tab.value === 2);
        case CATEGORIES.BIOEQUIVALENCE_VALIDATION:
        case CATEGORIES.QUALITY_VALIDATION:
        case CATEGORIES.PHARMACOVIGILANCE:
        case CATEGORIES.CLINICAL_VALIDATION:
        case CATEGORIES.CONTROL_LAB_VALIDATION:
        case CATEGORIES.INSPECTION_DEPARTMENT:
          return currentTabs.findIndex(tab => tab.value === 1);
        default:
          return 0;
      }
    }

    if (currentUserRole && parentStage && Object.keys(parentStage).length > 0) {
      let currentTabs = TABS;
      switch (currentUserRole) {
        case ROLES.technical_validator:
        case ROLES.business_validator:
          currentTabs = [
            { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
            { label: "Core Assessment", disabled: false, value: 1 },
            { label: "Pricing", disabled: false, value: 2 },
            { label: 'Additional Documents', disabled: false, value: 5 },
            { label: t('clarificationWaves.tabLabel'), disabled: false, value: 8 },
            { label: 'Audit Trail', disabled: false, value: 6 },
          ];
          break;
        case ROLES.core_Validator:
        case CATEGORIES.BIOEQUIVALENCE_VALIDATION:
        case CATEGORIES.QUALITY_VALIDATION:
        case CATEGORIES.PHARMACOVIGILANCE:
        case CATEGORIES.CLINICAL_VALIDATION:
        case CATEGORIES.CONTROL_LAB_VALIDATION:
        case CATEGORIES.INSPECTION_DEPARTMENT:
          currentTabs = [
            { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
            { label: "Core Assessment", disabled: false, value: 1 },
            { label: "Pricing", disabled: false, value: 2 },
            { label: 'Additional Documents', disabled: false, value: 5 },
            { label: t('clarificationWaves.tabLabel'), disabled: false, value: 8 },
            { label: 'Audit Trail', disabled: false, value: 6 },
            { label: 'Committee', disabled: !currentUserRole || currentUserRole !== ROLES.core_Validator, value: 7 },
          ];
          break;
        case ROLES.pricing:
          currentTabs = [
            { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
            { label: "Core Assessment", disabled: false, value: 1 },
            { label: "Pricing", disabled: false, value: 2 },
            { label: 'Additional Documents', disabled: false, value: 5 },
            { label: t('clarificationWaves.tabLabel'), disabled: false, value: 8 },
            { label: 'Audit Trail', disabled: false, value: 6 },
          ];
          break;
        case ROLES.RA:
          currentTabs = [
            { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
            { label: "Core Assessment", disabled: false, value: 1 },
            { label: "Pricing", disabled: false, value: 2 },
            { label: 'Additional Documents', disabled: false, value: 5 },
            { label: t('clarificationWaves.tabLabel'), disabled: false, value: 8 },
            { label: 'Audit Trail', disabled: false, value: 6 },
            { label: 'Committee', disabled: false, value: 7 },
          ];
          break;
        default:
          currentTabs = [
            { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
            { label: "Core Assessment", disabled: false, value: 1 },
            { label: "Pricing", disabled: false, value: 2 },
            { label: 'Additional Documents', disabled: false, value: 5 },
            { label: t('clarificationWaves.tabLabel'), disabled: false, value: 8 },
            { label: 'Audit Trail', disabled: false, value: 6 },
          ];
          break;
      }
      setTABS(currentTabs);
      
      // Only set default tab if we're not trying to open a specific tab from navigation
      if (location.state?.openTab !== undefined) {
        // Set the specific tab requested (e.g., Additional Documents)
        const additionalDocsIndex = currentTabs.findIndex(tab => 
          tab.label === 'Additional Documents'
        );
        if (additionalDocsIndex !== -1) {
          setActiveTab(additionalDocsIndex);
        }
      } else {
        // Set role-based default tab
        setActiveTab(openRelativeTab(currentTabs));
      }
    }
  }, [currentUserRole, parentStage, t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'clarifications' && TABS.length > 0) {
      const clarificationsTabIndex = TABS.findIndex(tab => tab.value === 8);
      if (clarificationsTabIndex !== -1) {
        setActiveTab(clarificationsTabIndex);
      }
    }
  }, [searchParams, TABS, setActiveTab]);

  if (loading || !currentUserRole) {
    return <div>Loading...</div>;
  }

  if (TABS.length === 0) {
    return <div>Loading...</div>;
  }
  
  return (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        truncate
        className='form-approve-tabs'
        shape="box"
      >
        {TABS.map((tab, index) => (
          <Tab
            key={index}
            label={
              <>
                {activeTab === index && <StarSolid style={{ marginRight: '8px' }} />}
                {tab.label}
              </>
            }
            disabled={tab.disabled } // || isIRB|| roleName === "Principal Investigator (PI)"
            className={`form-approve-tab ${activeTab === index ? 'selected' : ''}`}
          />
        ))}
      </Tabs>

      {TABS[activeTab].value === 0 && (
        <>
          <FormSections
            subForms={subForms}
            requestProgressId={requestProgressId}
            persona={persona}
            requestId={requestProgress?.request_id}
            filledForm={filledForm}
            requestProgress={requestProgress}
            children={children}
            handleAction={handleAction}
            roleName={currentUserRole}
            setComments={setComments}
            isReadOnly={true}
            enableAddComment={persona === "customer" ? false : undefined}
            hasProductDetails={!!requestDetails?.productDetails}
            hasManufacturersSite={!!requestDetails?.manufacturersSite}
            productDetailsData={requestDetails?.productDetails}
          />
        </>
      )}
      {TABS[activeTab].value === 1 && (
        <>
          {(currentUserRole === ROLES.core_Validator ||
            CATEGORIES.ECTD_ASSESSMENT_CATEGORIES.includes(currentUserRole) ||
            currentUserRole === CATEGORIES.CONTROL_LAB_VALIDATION ||
            currentUserRole === CATEGORIES.INSPECTION_DEPARTMENT) ?
            <CoreECTD
              persona={persona}
              requestProgressId={requestProgressId}
              ectdFileId={requestProgress?.request?.ectd_file_id}
              role={currentUserRole}
              showCommitteeReviewButtons={true}
              requestId={requestProgress?.request_id}
              ectdTreeComponentProps={{
                treeData,
                requestProgressId,
                requestProgressStatus: requestProgress?.status,
                comments,
                setComments,
                requestId: requestProgress?.request?.id,
                ectdVersion: requestDetails?.ectd_version,
              }}
              laboratoryUploadComponentProps={{
                requestId: requestProgress?.request?.id,
                requestProgressId,
                persona,
                comments: labComments,
                setComments: setLabComments,
                onUploadSuccess: () => setFileUploadTrigger(prev => prev + 1),
              }}
              laboratoryViewComponentProps={{
                requestProgressId,
                persona,
                comments: labComments,
                setComments: setLabComments,
              }}
              CommitteeReviewButtonsComponentProps={{
                requestProgressId,
                persona: "customer",
                fileUploadTrigger,
              }}
              inspectionUploadComponentProps={{
                requestId: requestProgress?.request_id,
                requestProgressId,
                persona,
                comments: inspectionComments,
                setComments: setInspectionComments,
                onUploadSuccess: () => setFileUploadTrigger(prev => prev + 1),
              }}
              inspectionViewComponentProps={{
                requestProgressId,
                persona,
                comments: inspectionComments,
                setComments: setInspectionComments,
              }}
              MembersStatusesComponentProps={{
                requestProgressId,
                requestProgress,
                requestId: requestProgress?.request_id,
                children,
                persona,
              }}
            /> :
            <>
            <Ectd
            ectdFileId={requestProgress?.request?.ectd_file_id}
            requestId={requestProgress?.request?.id}
            role={currentUserRole}
            ectdTreeComponentProps={{
              treeData,
              requestProgressId,
              requestProgressStatus: requestProgress?.status,
              comments,
              setComments,
              requestId: requestProgress?.request?.id,
              ectdVersion: requestDetails?.ectd_version,
            }}
            />
            {currentUserRole === ROLES.technical_validator &&
            requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.technical_validator].ACTIVE && (
              <ActionButtons
                requestProgressId={requestProgressId}
                requestProgress={requestProgress}
                requestId={requestProgress?.request_id}
                children={children}
                handleAction={handleAction}
                prevComments={comments}
              />
            )
            }
            {CATEGORIES.ECTD_ASSESSMENT_CATEGORIES.includes(currentUserRole) &&
              requestProgress?.status ===
              STATUSES.ROLE_STATUSES[ROLES.committee].ACTIVE && (
                <CommitteeReviewButtons
                  requestProgressId={requestProgressId}
                  persona="customer"
                  fileUploadTrigger={fileUploadTrigger}
                />
            )}
            </>      
          }
        </>
      )}
      {TABS[activeTab].value === 2 && (
        <>
        <DrugPricingFormView 
          drugPricingFormId={requestDetails.drug_pricing_form_id}
          persona={persona}
          role={currentUserRole}
          requestProgressStatus={requestProgress?.status}
          comments={comments}
          setComments={setComments}
        />
        {currentUserRole === ROLES.pricing &&
        requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.pricing].ACTIVE && (
          <ActionButtons
            requestProgressId={requestProgressId}
            requestProgress={requestProgress}
            requestId={requestProgress?.request_id}
            children={children}
            handleAction={handleAction}
            prevComments={comments}
          />
        )
        }
        </>
      )}
      {TABS[activeTab].value === 5 && (
        <AdditionalDocuments 
          requestId={requestProgress?.request_id}
          certificateFileId={requestProgress?.request?.certificate_file_id}
          role={currentUserRole}
          persona={persona}
          requestProgressStatus={requestProgress?.status}
          comments={additionalDocsComments}
          setComments={setAdditionalDocsComments}
        />
      )}
      {TABS[activeTab].value === 7 && (
        <Committee
          requestId={requestProgress?.request_id}
          requestProgressId={requestProgressId}
          role={currentUserRole}
          requestProgressStatus={requestProgress?.status}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeTab].value === 8 && (
        <ClarificationWaves
          subForms={subForms}
          requestProgressId={requestProgressId}
          persona={persona}
          requestId={requestProgress?.request_id}
          filledForm={filledForm}
          requestProgress={requestProgress}
          requestDetails={requestDetails}
          roleName={currentUserRole}
          parentStage={parentStage}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeTab].value === 6 && (
        <AuditTrails persona={persona} requestID={requestProgress?.request_id}
        role={currentUserRole} />
      )}
      {((currentUserRole === ROLES.core_Validator &&
        requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.core_Validator].ACTIVE) ||
        (currentUserRole === ROLES.RA &&
        requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.RA].ACTIVE)
        ) && (
          <ActionButtons
            requestProgressId={requestProgressId}
            requestProgress={requestProgress}
            requestId={requestProgress?.request_id}
            children={updatedChildren}
            handleAction={handleAction}
            prevComments={comments}
            isRA={currentUserRole === ROLES.RA}
          />
        )
      }
    </>
  );
};

export default FormApprove;
